import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Upload } from 'antd';
import { CloseOutlined, FileOutlined, InboxOutlined } from '@ant-design/icons';
import { ScanLine } from 'lucide-react';

import { PageHeader } from '../../../components/shared';
import { Badge, Button, Empty, Field, Select, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useRoutePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { dungLuongVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { useLuaChonModelAi } from '../../ai-model/hooks/useAiModels';
import { NHAN_CHE_DO } from '../../cau-hinh-ocr/constants';
import { useDanhSachLoaiChungTu, useLoaiChungTuTheoMa } from '../../cau-hinh-ocr/hooks/useLoaiChungTu';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { loiTheoConTro } from '../../vai-tro/nghiep-vu/vai-tro';
import { TypeBadge } from '../components/badges';
import { useGhiChungTu } from '../hooks/useChungTu';
import { ACCEPT, chiSoTepLoi, kiemTepNhanDang, type LoiTep } from '../nghiep-vu/tai-len';
import { OCR_LANGUAGES, type OcrLanguage } from '../types';

/** `Select` không giữ được `undefined` làm một lựa chọn: chuỗi rỗng là "theo loại chứng từ". */
const THEO_LOAI = '';

const KHOA_NGON_NGU = { vi: 'ocr.ngonNgu.vi', en: 'ocr.ngonNgu.en', 'vi+en': 'ocr.ngonNgu.viEn' } as const;
const KHOA_LOI_TEP: Record<LoiTep, 'nhanDang.loiDinhDang' | 'nhanDang.loiQuaLon'> = {
  'sai-dinh-dang': 'nhanDang.loiDinhDang',
  'qua-lon': 'nhanDang.loiQuaLon',
};

/**
 * Tải chứng từ lên nhận dạng theo một loại chứng từ (`/xu-ly/nhan-dang/:schemaCode`, phân hệ
 * `OCR_RECOGNIZE`), gửi `POST /api/v1/m/ocr/documents/upload`. Nhận dạng chạy nền ở worker của
 * module; gửi xong chuyển sang màn chi tiết chứng từ, màn đó tự cập nhật theo giai đoạn.
 */
export function NhanDangView() {
  const { schemaCode } = useParams<{ schemaCode: string }>();
  const navigate = useDieuHuong();
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const coNhanDang = useRoutePermission('CREATE');

  const q = useLoaiChungTuTheoMa(schemaCode);
  const ds = useDanhSachLoaiChungTu();
  const models = useLuaChonModelAi();
  const ghi = useGhiChungTu();

  const [tep, setTep] = useState<File[]>([]);
  const [modelDien, setModelDien] = useState(THEO_LOAI);
  const [modelDoc, setModelDoc] = useState(THEO_LOAI);
  const [ngonNgu, setNgonNgu] = useState<OcrLanguage>('vi');

  const kiem = useMemo(() => kiemTepNhanDang(tep), [tep]);
  const dangDung = useMemo(() => (ds.data ?? []).filter((s) => s.is_active), [ds.data]);
  const loiServer = loiTheoConTro((ghi.taiLen.error as ApiError | null)?.details);

  const schema = q.data;

  if (q.isLoading) {
    return (
      <div className="flex h-full flex-col gap-4 bg-subtle p-4">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (q.error || !schema) {
    const khongThay = (q.error as ApiError | null)?.status === 404 || !q.error;
    return (
      <div className="flex h-full flex-col bg-subtle p-4">
        <div className="rounded-lg border border-default bg-surface shadow-sm">
          {khongThay ? (
            <Empty
              icon={ScanLine}
              title={t('nhanDang.khongTimThayKhuon')}
              description={t('nhanDang.khongTimThayKhuonMo', { ma: schemaCode ?? '' })}
              action={
                <Button variant="primary" size="md" onClick={() => navigate('/xu-ly/nhan-dang')}>
                  {t('nhanDang.chonKhuonKhac')}
                </Button>
              }
            />
          ) : (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">{t('nhanDang.loiKhuon', { loi: dichLoi(q.error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const haiBuoc = schema.extraction_mode === 'OCR_THEN_MAP';
  const luaChon = models.data ?? [];
  const optDien = [
    { value: THEO_LOAI, label: t('nhanDang.theoLoaiChungTu') },
    ...luaChon.filter((m) => m.can_map).map((m) => ({ value: m.key, label: m.label })),
  ];
  const optDoc = [
    { value: THEO_LOAI, label: t('nhanDang.theoLoaiChungTu') },
    ...luaChon.filter((m) => m.can_read_pages).map((m) => ({ value: m.key, label: m.label })),
  ];

  const themTep = (moi: File[]) => {
    ghi.taiLen.reset();
    setTep((cu) => {
      const daCo = new Set(cu.map((f) => `${f.name}:${f.size}`));
      return [...cu, ...moi.filter((f) => !daCo.has(`${f.name}:${f.size}`))];
    });
  };
  const boTep = (i: number) => {
    ghi.taiLen.reset();
    setTep((cu) => cu.filter((_f, j) => j !== i));
  };

  const gui = () => {
    if (!kiem.hopLe) return;
    ghi.taiLen.mutate(
      {
        files: tep,
        schema_id: schema.id,
        language: ngonNgu,
        ...(modelDien ? { ocr_provider: modelDien } : {}),
        ...(haiBuoc && modelDoc ? { ocr_reader_provider: modelDoc } : {}),
      },
      {
        onSuccess: (kq) => {
          toast.success(t('nhanDang.daTaiLen'));
          navigate(`/xu-ly/chung-tu/${kq.document_id}`);
        },
      },
    );
  };

  const loiCuaTep = (i: number): string | undefined => {
    const cuaMay = kiem.theoTep.find((x) => x.chiSo === i);
    if (cuaMay) return t(KHOA_LOI_TEP[cuaMay.loi]);
    const k = Object.keys(loiServer).find((p) => chiSoTepLoi(p) === i);
    return k ? loiServer[k] : undefined;
  };
  const loiChung = kiem.quaNhieuTep
    ? t('nhanDang.loiQuaNhieu')
    : kiem.tongQuaLon
      ? t('nhanDang.loiTongQuaLon')
      : loiServer['/files'];
  const loiTheoTep = Object.keys(loiServer).some((p) => chiSoTepLoi(p) !== null || p === '/files');
  const loiKhac = ghi.taiLen.error && !loiTheoTep ? dichLoi(ghi.taiLen.error) : null;

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nhanDang.chonKhuonTieuDe'), icon: <ScanLine size={12} />, onClick: () => navigate('/xu-ly/nhan-dang') },
            { label: schema.name },
          ]}
          title={
            <span className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="truncate">{schema.name}</span>
              <span className="shrink-0 rounded bg-subtle px-2 py-0.5 font-mono text-xs font-normal text-content-secondary">
                {schema.code}
              </span>
              <TypeBadge type={schema.type} />
            </span>
          }
          actions={
            dangDung.length >= 2 && (
              <Select
                showSearch
                className="w-full sm:w-64"
                aria-label={t('nhanDang.doiKhuon')}
                value={schema.code}
                optionFilterProp="label"
                options={dangDung.map((s) => ({ value: s.code, label: s.name }))}
                onChange={(value) => navigate(`/xu-ly/nhan-dang/${encodeURIComponent(value as string)}`)}
              />
            )
          }
        />

        {!schema.is_active && (
          <div role="status" className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-700">
            {t('nhanDang.khuonDaTat')}
          </div>
        )}

        <div className="grid grid-cols-12 gap-4">
          {/* Vùng chọn tệp. */}
          <section
            aria-label={t('nhanDang.tepDaChon')}
            className="col-span-12 flex flex-col gap-4 rounded-lg border border-default bg-surface p-5 shadow-sm lg:col-span-8"
          >
            {coNhanDang ? (
              <Upload.Dragger
                multiple
                accept={ACCEPT}
                showUploadList={false}
                disabled={ghi.taiLen.isPending || !schema.is_active}
                beforeUpload={(_f, ds) => {
                  // Gọi một lần cho mỗi tệp với cùng `ds`: chỉ nhận ở tệp cuối để không thêm lặp.
                  if (_f === ds[ds.length - 1]) themTep(ds as File[]);
                  return false;
                }}
              >
                <div className="flex flex-col items-center px-6 py-8 text-center">
                  <InboxOutlined className="mb-3 text-3xl text-content-muted" />
                  <p className="text-sm font-medium text-content-primary">{t('nhanDang.keoThaTep')}</p>
                  <p className="mt-1 text-xs text-content-muted">{t('nhanDang.gioiHanTep')}</p>
                </div>
              </Upload.Dragger>
            ) : (
              <p className="rounded border border-dashed border-strong bg-subtle px-4 py-6 text-center text-sm text-content-secondary">
                {t('nhanDang.chiXem')}
              </p>
            )}

            {tep.length > 0 && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="text-sm font-semibold text-content-primary">{t('nhanDang.tepDaChon')}</h2>
                  <span className="text-xs text-content-muted">{t('nhanDang.tepChinhMo')}</span>
                </div>
                <ul className="divide-y divide-[var(--border-default)] rounded border border-default">
                  {tep.map((f, i) => {
                    const loi = loiCuaTep(i);
                    return (
                      <li key={`${f.name}:${f.size}`} className="flex items-start gap-3 px-3 py-2">
                        <FileOutlined className="mt-1 shrink-0 text-content-muted" />
                        <div className="min-w-0 flex-1">
                          <p className="flex min-w-0 items-center gap-2">
                            <span className="truncate text-sm text-content-primary" title={f.name}>
                              {f.name}
                            </span>
                            {i === 0 && <Badge variant="neutral">{t('chungTu.tepChinh')}</Badge>}
                          </p>
                          <p className="text-2xs text-content-muted">{dungLuongVN(f.size, locale)}</p>
                          {loi && <p className="mt-0.5 text-xs text-danger-700">{loi}</p>}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t('nhanDang.boTep', { ten: f.name })}
                          title={t('nhanDang.boTep', { ten: f.name })}
                          disabled={ghi.taiLen.isPending}
                          onClick={() => boTep(i)}
                        >
                          <CloseOutlined />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
                {loiChung && <p className="text-xs text-danger-700">{loiChung}</p>}
              </div>
            )}
          </section>

          {/* Cách nhận dạng. */}
          <section
            aria-label={t('nhanDang.caiDat')}
            className="col-span-12 flex flex-col gap-4 rounded-lg border border-default bg-surface p-5 shadow-sm lg:col-span-4"
          >
            <h2 className="text-sm font-semibold text-content-primary">{t('nhanDang.caiDat')}</h2>
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
              <dt className="text-content-muted">{t('nhanDang.cheDo')}</dt>
              <dd className="text-content-secondary">{t(NHAN_CHE_DO[schema.extraction_mode])}</dd>
              <dt className="text-content-muted">{t('chungTu.truongDuLieu')}</dt>
              <dd className="text-content-secondary">
                {t('nhanDang.soTruong', { so: schema.fields.length })} · {t('nhanDang.soBang', { so: schema.tables.length })}
              </dd>
            </dl>

            <Field label={t('nhanDang.modelDien')} hint={t('nhanDang.modelDienMo')} error={loiServer['/ocr_provider']}>
              {(p) => (
                <Select<string>
                  {...p}
                  className="w-full"
                  showSearch
                  optionFilterProp="label"
                  loading={models.isLoading}
                  value={modelDien}
                  onChange={setModelDien}
                  options={optDien}
                />
              )}
            </Field>

            {haiBuoc && (
              <Field label={t('nhanDang.modelDoc')} hint={t('nhanDang.modelDocMo')} error={loiServer['/ocr_reader_provider']}>
                {(p) => (
                  <Select<string>
                    {...p}
                    className="w-full"
                    showSearch
                    optionFilterProp="label"
                    loading={models.isLoading}
                    value={modelDoc}
                    onChange={setModelDoc}
                    options={optDoc}
                  />
                )}
              </Field>
            )}

            <Field label={t('nhanDang.ngonNgu')} error={loiServer['/language']}>
              {(p) => (
                <Select<OcrLanguage>
                  {...p}
                  className="w-full"
                  value={ngonNgu}
                  onChange={setNgonNgu}
                  options={OCR_LANGUAGES.map((v) => ({ value: v, label: t(KHOA_NGON_NGU[v]) }))}
                />
              )}
            </Field>

            {loiKhac && <LoiTrongHop>{loiKhac}</LoiTrongHop>}

            {coNhanDang && (
              <Button
                variant="primary"
                size="md"
                className="ai-glow mt-auto"
                disabled={!kiem.hopLe || !schema.is_active}
                loading={ghi.taiLen.isPending}
                onClick={gui}
              >
                <ScanLine size={15} className="ai-sparkle" />
                {ghi.taiLen.isPending
                  ? t('nhanDang.dangGui')
                  : tep.length > 0
                    ? t('nhanDang.batDauNhanDangSo', { so: tep.length })
                    : t('nhanDang.batDauNhanDang')}
              </Button>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
